import { compressToUTF16, decompressFromUTF16 } from "lz-string";
import { MEAL_TYPES } from "../modules/platform/components/common/Meal_Types";

export const saveToLocalStorageWithExpiry = (
  key: string,
  value: any,
  expiryInMinutes: number
) => {
  const now = new Date();
  const item = {
    value,
    expiry: now.getTime() + expiryInMinutes * 60 * 1000, // Calculate expiry time
  };
  localStorage.setItem(key, JSON.stringify(item));
};

export const getFromLocalStorageWithExpiry = (key: string): any | null => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;

  try {
    const item = JSON.parse(itemStr);
    const now = new Date();

    // Check if the item has expired
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(key); // Remove expired item
      return null;
    }

    return item.value;
  } catch (error) {
    console.error("Error parsing localStorage data", error);
    return null;
  }
};

/**
 * The same expiring cache, compressed: for one value too big to store as
 * plain JSON. localStorage holds about 5 MB per site, counted in UTF-16
 * characters, and the biomarker catalogue (`GetAllBiomarkers`, 3,800 items
 * with a ~2,500-character description each) is 6 MB of JSON — 12 MB in
 * storage terms. lz-string's UTF-16 output brings it to about 1.8 MB.
 *
 * Compressing 6 MB takes ~0.7 s on the main thread, so the write waits for an
 * idle moment (or a short timeout where `requestIdleCallback` is missing).
 * The read decompresses in ~0.1 s. Everything else keeps the plain helpers:
 * only the one oversized value pays for compression.
 */
export const saveToLocalStorageCompressedWithExpiry = (
  key: string,
  value: any,
  expiryInMinutes: number
) => {
  const write = () => {
    try {
      const item = {
        value,
        expiry: new Date().getTime() + expiryInMinutes * 60 * 1000,
      };
      localStorage.setItem(key, compressToUTF16(JSON.stringify(item)));
    } catch (error) {
      // Out of quota or storage blocked: the value is just not remembered,
      // which only costs the next load a refetch.
      console.error(`Could not cache ${key}`, error);
    }
  };
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    (window as any).requestIdleCallback(write, { timeout: 5000 });
  } else {
    setTimeout(write, 0);
  }
};

export const getFromLocalStorageCompressedWithExpiry = (key: string): any | null => {
  const compressed = localStorage.getItem(key);
  if (!compressed) return null;

  try {
    const itemStr = decompressFromUTF16(compressed);
    // Not compressed data — a cache written before compression, say. Treat
    // it as a miss; the refetch overwrites it.
    if (!itemStr) {
      localStorage.removeItem(key);
      return null;
    }
    const item = JSON.parse(itemStr);
    if (new Date().getTime() > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  } catch (error) {
    console.error("Error reading compressed localStorage data", error);
    localStorage.removeItem(key);
    return null;
  }
};

export const findMealType = (type: number) =>
  MEAL_TYPES.find(
    (meal: { type: number; label: string; machineName: string }) =>
      meal.type === type
  )?.label;

export const enAgentType: { [key: number]: string } = {
  1: "Dashboard",
  2: "Cheatsheet",
};

export const getSelectedAgentTypes = (bitmaskValue: number) => {
  return Object.keys(enAgentType)
    .filter((key) => bitmaskValue & +key)
    .map((key) => enAgentType[+key]);
};

export const validateEmail = (email: string) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

export const validateText = (str: string) => {
  return str.trim() !== "";
};

export const validateZipCode = (zipCode: string) => {
  const regex = /^\d{5}$/; // Matches 12345 or 12345-6789
  return regex.test(zipCode);
};

export const isValidDate = (date: Date): boolean => {
  return date instanceof Date && !isNaN(date.getTime());
};
